// Per-call WebRTC media quality sampler for RingCentral calls.
//
// WHY: users reported audio "cutting in and out" every day in the same window
// (Sept 2026) and CallErrorLogs only held call SETUP failures (allocate /
// register-network / dial). Once a call is connected nothing was recorded, so
// the mid-call complaint had no numbers behind it. The setup-failure rows
// pointed at the office network (many users, two unrelated hosts, same minute),
// but that stayed a reconstruction. This samples RTCPeerConnection.getStats()
// every few seconds while the call is live and, at call end, produces one
// summary row (stage "quality") so packet loss / jitter / RTT are queryable
// alongside the failures.
//
// Design notes:
// - Never throws and never touches the media path: getStats() is read-only.
// - Holds its own RTCPeerConnection reference. The service nulls
//   `currentSession` BEFORE emitting the terminal status on several paths, so
//   reading the session at call end would find nothing.
// - Sampling and summarising are separate: `stopSampling()` is safe to call
//   from any timer-teardown path; `summary()` is read once when the terminal
//   status is known.

const SAMPLE_MS = 5000;

// Thresholds for a "bad" interval (per SAMPLE_MS window). Loss >= 5% is where
// Opus concealment stops hiding gaps; RTT >= 400ms and jitter >= 50ms are the
// usual "sounds choppy / talk-over" boundaries for a voice call.
const BAD_LOSS_PCT = 5;
const BAD_RTT_MS = 400;
const BAD_JITTER_MS = 50;

const pct = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : 0);
const ms = (sec) =>
  typeof sec === "number" && Number.isFinite(sec) ? Math.round(sec * 1000) : null;
const avg = (arr) =>
  arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
const max = (arr) => (arr.length ? Math.max(...arr) : null);

export class CallQualityMonitor {
  constructor(pc, meta = {}) {
    this.pc = pc || null;
    this.meta = meta;
    this._timer = null;
    this._startedAt = Date.now();
    this._stoppedAt = null;
    this._prev = null;
    this._first = null;
    this._last = null;
    this._intervals = []; // one entry per delta between consecutive samples
    this._transport = null;
    this._sampling = false;
    // Media-path failure signals, kept separately from the periodic samples so
    // the "how did this call end" verdict can be built at flush time:
    //  - ICE going disconnected/failed BEFORE the call ended = the audio link
    //    broke first; whatever BYE followed was a consequence, not a hang-up.
    //  - the local mic track ending = headset unplugged / device removed.
    this._iceTroubleAt = null;
    this._iceTroubleState = null;
    this._iceStates = [];
    this._finalIceState = null;
    this._micLostAt = null;
    this._listeners = [];
  }

  start() {
    if (!this.pc || this._timer) return;
    this._sampling = true;
    this._attachFailureListeners();
    this._timer = setInterval(() => this._sample(), SAMPLE_MS);
    // First read right away so a short call still yields one sample.
    this._sample();
  }

  stopSampling() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    if (this._sampling) {
      this._sampling = false;
      this._stoppedAt = Date.now();
      try {
        this._finalIceState = this.pc?.iceConnectionState || null;
      } catch {
        /* ignore */
      }
    }
    this._detachFailureListeners();
  }

  _attachFailureListeners() {
    const pc = this.pc;
    if (!pc || typeof pc.addEventListener !== "function") return;
    const on = (target, ev, fn) => {
      try {
        target.addEventListener(ev, fn);
        this._listeners.push([target, ev, fn]);
      } catch {
        /* ignore */
      }
    };
    const offset = () => Math.round((Date.now() - this._startedAt) / 1000);
    on(pc, "iceconnectionstatechange", () => {
      const st = pc.iceConnectionState;
      this._iceStates.push(`${offset()}s:${st}`);
      // "closed" is the normal end; only disconnected/failed mean trouble.
      if ((st === "disconnected" || st === "failed") && this._iceTroubleAt == null) {
        this._iceTroubleAt = offset();
        this._iceTroubleState = st;
        console.warn("[RC quality] ICE", st, "at", this._iceTroubleAt, "s");
      }
    });
    on(pc, "connectionstatechange", () => {
      const st = pc.connectionState;
      if (st === "failed" && this._iceTroubleAt == null) {
        this._iceTroubleAt = offset();
        this._iceTroubleState = "failed";
      }
    });
    try {
      for (const sender of pc.getSenders()) {
        const track = sender.track;
        if (track && track.kind === "audio") {
          on(track, "ended", () => {
            if (this._micLostAt == null) {
              this._micLostAt = offset();
              console.warn("[RC quality] local mic track ended at", this._micLostAt, "s");
            }
          });
        }
      }
    } catch {
      /* ignore */
    }
  }

  _detachFailureListeners() {
    for (const [target, ev, fn] of this._listeners) {
      try {
        target.removeEventListener(ev, fn);
      } catch {
        /* ignore */
      }
    }
    this._listeners = [];
  }

  // Did the audio path break before the call ended? Used to reclassify a
  // "remote hung up" end (RingCentral's media server times out on missing RTP
  // and sends BYE, which looks like the far end hanging up) as a drop.
  mediaFailureBeforeEnd() {
    if (this._micLostAt != null) {
      return { failed: true, reason: `mic track ended at ${this._micLostAt}s` };
    }
    if (this._iceTroubleAt != null) {
      return {
        failed: true,
        reason: `ICE ${this._iceTroubleState} at ${this._iceTroubleAt}s`,
      };
    }
    // Last two 5s windows before the end: no outbound packets at all, or
    // heavy loss in either direction, = the link was already dead/dying.
    const tail = this._intervals.slice(-2);
    if (tail.length) {
      const stalled = tail.some((i) => i.outStalled);
      const heavy = tail.some(
        (i) => (i.outLossPct ?? 0) >= 15 || (i.inLossPct ?? 0) >= 15,
      );
      if (stalled) {
        return { failed: true, reason: "outbound audio stalled in the last 10s" };
      }
      if (heavy) {
        return { failed: true, reason: "heavy packet loss in the last 10s" };
      }
    }
    return { failed: false, reason: null };
  }

  get micLostAt() {
    return this._micLostAt;
  }

  get isSampling() {
    return this._sampling;
  }

  async _sample() {
    const pc = this.pc;
    if (!pc) return;
    let report;
    try {
      if (pc.connectionState === "closed" || pc.signalingState === "closed") {
        this.stopSampling();
        return;
      }
      report = await pc.getStats();
    } catch {
      return; // transient; keep the timer running
    }
    if (this._sampling === false) return;

    const s = { t: Date.now() };
    const pairs = {};
    const candidates = {};
    let transport = null;
    try {
      report.forEach((r) => {
        const isAudio =
          r.kind === "audio" || r.mediaType === "audio" || (!r.kind && !r.mediaType);
        if (r.type === "outbound-rtp" && isAudio) {
          s.outSent = r.packetsSent;
          s.outBytes = r.bytesSent;
        } else if (r.type === "remote-inbound-rtp" && isAudio) {
          // What RingCentral's media server reports back about OUR outgoing audio.
          s.outLost = r.packetsLost;
          s.outJitter = r.jitter;
          s.rtt = r.roundTripTime;
        } else if (r.type === "inbound-rtp" && isAudio) {
          s.inRecv = r.packetsReceived;
          s.inLost = r.packetsLost;
          s.inJitter = r.jitter;
          s.concealed = r.concealedSamples;
          s.totalSamples = r.totalSamplesReceived;
        } else if (r.type === "candidate-pair") {
          pairs[r.id] = r;
        } else if (r.type === "local-candidate" || r.type === "remote-candidate") {
          candidates[r.id] = r;
        } else if (r.type === "transport") {
          transport = r;
        }
      });
    } catch {
      return;
    }

    // Selected ICE pair: prefer the transport's pointer, else the nominated
    // succeeded pair. Tells us whether media is UDP or forced onto TCP/relay.
    let pair = transport?.selectedCandidatePairId
      ? pairs[transport.selectedCandidatePairId]
      : null;
    if (!pair) {
      pair = Object.values(pairs).find(
        (p) => p.state === "succeeded" && (p.nominated || p.selected),
      );
    }
    if (pair) {
      if (typeof pair.currentRoundTripTime === "number" && s.rtt == null) {
        s.rtt = pair.currentRoundTripTime;
      }
      s.outBitrate = pair.availableOutgoingBitrate;
      const lc = candidates[pair.localCandidateId];
      const rc = candidates[pair.remoteCandidateId];
      this._transport = {
        local: lc?.candidateType || null,
        remote: rc?.candidateType || null,
        protocol: lc?.protocol || rc?.protocol || null,
        network: lc?.networkType || null,
        relay: lc?.candidateType === "relay" || rc?.candidateType === "relay",
      };
    }

    if (!this._first) this._first = s;
    this._last = s;

    const p = this._prev;
    if (p) {
      const dSent = (s.outSent ?? 0) - (p.outSent ?? 0);
      const dOutLost = (s.outLost ?? 0) - (p.outLost ?? 0);
      const dRecv = (s.inRecv ?? 0) - (p.inRecv ?? 0);
      const dInLost = (s.inLost ?? 0) - (p.inLost ?? 0);
      const dConcealed = (s.concealed ?? 0) - (p.concealed ?? 0);
      const dSamples = (s.totalSamples ?? 0) - (p.totalSamples ?? 0);
      const iv = {
        at: Math.round((s.t - this._startedAt) / 1000),
        outLossPct: dSent > 0 ? pct(Math.max(dOutLost, 0), dSent) : null,
        inLossPct:
          dRecv + dInLost > 0 ? pct(Math.max(dInLost, 0), dRecv + dInLost) : null,
        concealedPct: dSamples > 0 ? pct(Math.max(dConcealed, 0), dSamples) : null,
        rttMs: ms(s.rtt),
        outJitterMs: ms(s.outJitter),
        inJitterMs: ms(s.inJitter),
        // No packets sent in a whole window while connected = mic/track stalled
        // or the uplink is fully blocked, which the far end hears as silence
        // ("they can't hear me"). Distinct from loss.
        outStalled: dSent === 0 && s.outSent != null,
      };
      iv.bad =
        (iv.outLossPct ?? 0) >= BAD_LOSS_PCT ||
        (iv.inLossPct ?? 0) >= BAD_LOSS_PCT ||
        (iv.rttMs ?? 0) >= BAD_RTT_MS ||
        (iv.outJitterMs ?? 0) >= BAD_JITTER_MS ||
        (iv.inJitterMs ?? 0) >= BAD_JITTER_MS ||
        iv.outStalled;
      this._intervals.push(iv);
      if (iv.bad) {
        console.warn("[RC quality] bad interval", iv);
      }
    }
    this._prev = s;
  }

  // One row's worth of numbers. Safe to call any time; null if nothing was read.
  summary(endStatus) {
    const f = this._first;
    const l = this._last;
    if (!f || !l) return null;
    const endedAt = this._stoppedAt || Date.now();
    const durationSec = Math.max(0, Math.round((endedAt - this._startedAt) / 1000));
    const ivs = this._intervals;

    const outSent = (l.outSent ?? 0) - (f.outSent ?? 0);
    const outLost = Math.max((l.outLost ?? 0) - (f.outLost ?? 0), 0);
    const inRecv = (l.inRecv ?? 0) - (f.inRecv ?? 0);
    const inLost = Math.max((l.inLost ?? 0) - (f.inLost ?? 0), 0);
    const concealed = Math.max((l.concealed ?? 0) - (f.concealed ?? 0), 0);
    const samples = Math.max((l.totalSamples ?? 0) - (f.totalSamples ?? 0), 0);

    const rtts = ivs.map((i) => i.rttMs).filter((v) => v != null);
    const outJ = ivs.map((i) => i.outJitterMs).filter((v) => v != null);
    const inJ = ivs.map((i) => i.inJitterMs).filter((v) => v != null);
    const outLossIv = ivs.map((i) => i.outLossPct).filter((v) => v != null);
    const inLossIv = ivs.map((i) => i.inLossPct).filter((v) => v != null);
    const badIvs = ivs.filter((i) => i.bad);
    const stalled = ivs.filter((i) => i.outStalled).length;

    const out = {
      sent: outSent,
      lost: outLost,
      lossPct: pct(outLost, outSent + outLost),
      maxIntervalLossPct: max(outLossIv),
      jitterAvgMs: avg(outJ),
      jitterMaxMs: max(outJ),
      stalledIntervals: stalled,
    };
    const inn = {
      received: inRecv,
      lost: inLost,
      lossPct: pct(inLost, inRecv + inLost),
      maxIntervalLossPct: max(inLossIv),
      jitterAvgMs: avg(inJ),
      jitterMaxMs: max(inJ),
      concealedPct: pct(concealed, samples),
    };
    const rtt = { avgMs: avg(rtts), maxMs: max(rtts) };

    // Verdict. "poor" = the user would have noticed; "degraded" = marginal.
    let verdict = "ok";
    const worstLoss = Math.max(out.lossPct, inn.lossPct);
    const worstIvLoss = Math.max(
      out.maxIntervalLossPct ?? 0,
      inn.maxIntervalLossPct ?? 0,
    );
    if (
      worstLoss >= 5 ||
      worstIvLoss >= 15 ||
      inn.concealedPct >= 10 ||
      stalled > 0 ||
      (ivs.length > 0 &&
        badIvs.length >= Math.max(3, Math.ceil(ivs.length * 0.25)))
    ) {
      verdict = "poor";
    } else if (
      worstLoss >= 2 ||
      badIvs.length >= 1 ||
      (rtt.maxMs ?? 0) >= BAD_RTT_MS ||
      inn.concealedPct >= 5
    ) {
      verdict = "degraded";
    }

    let browser = null;
    try {
      const c = navigator?.connection;
      if (c) {
        browser = {
          effectiveType: c.effectiveType ?? null,
          downlinkMbps: c.downlink ?? null,
          rttMs: c.rtt ?? null,
        };
      }
    } catch {
      /* not supported */
    }

    const t = this._transport;
    const line =
      `${verdict}: ${durationSec}s, ${ivs.length} intervals, ${badIvs.length} bad` +
      ` | out loss ${out.lossPct}% (max ${out.maxIntervalLossPct ?? 0}%)` +
      (stalled ? `, ${stalled} stalled` : "") +
      ` | in loss ${inn.lossPct}% (max ${inn.maxIntervalLossPct ?? 0}%), concealed ${inn.concealedPct}%` +
      ` | rtt avg ${rtt.avgMs ?? "-"}/max ${rtt.maxMs ?? "-"}ms` +
      ` | jitter out ${out.jitterMaxMs ?? "-"}/in ${inn.jitterMaxMs ?? "-"}ms` +
      (t
        ? ` | ${t.protocol || "?"} ${t.local || "?"}->${t.remote || "?"}${t.relay ? " (relay)" : ""}`
        : "");

    return {
      verdict,
      line,
      endStatus: endStatus || null,
      durationSec,
      intervals: ivs.length,
      badIntervals: badIvs.length,
      // Offsets (seconds into the call) of bad windows, so a complaint like
      // "it cut out near the end" can be matched to a moment.
      badAtSec: badIvs.slice(0, 40).map((i) => i.at),
      out,
      in: inn,
      rtt,
      transport: t,
      media: {
        finalIceState: this._finalIceState,
        iceTroubleAt: this._iceTroubleAt,
        iceTroubleState: this._iceTroubleState,
        iceStates: this._iceStates.slice(0, 20),
        micLostAt: this._micLostAt,
      },
      browser,
      ...this.meta,
    };
  }
}
